import { useCallback } from 'react';
import { useWfUserStore } from '../stores/WfUserStore';
import useWfUserRepo from '../repository/WfUserRepo';
import { WfUser } from '../models/WfUser';

// For Okinichi members, overwrite branch code to '  ' (aliasBranchCode).
// This must be applied to every WfUser object obtained from the backend
// (initial login fetch AND subsequent updates such as location refresh),
// otherwise a later raw fetch will revert branchCode back to '30' and break
// the sales office/section/squad lookups that rely on the alias code.
function applyOkinichiBranchAlias<T extends WfUser | null>(user: T): T {
  if (user && user.branchCode === '30' && user.salesOfficeCode === '306680') {
    user.branchCode = '  ';
  }
  return user;
}

function useWfUserService() {
  const {
    getUserRequest,
    getUsersByLocationRequest,
    patchUserLocationRequest,
    getUsersByNameLdapPhoneNoRequest,
  } = useWfUserRepo();
  const { setUser, setUsers, setIsAdmin } = useWfUserStore();

  const wfGetUser = useCallback(async () => {
    await getUserRequest().then((user) => {
      applyOkinichiBranchAlias(user);

      setUser(user);

      const jobPositionValue = user?.jobPosition;

      const jobPosition =
        jobPositionValue !== null && !isNaN(Number(jobPositionValue))
          ? Number(jobPositionValue)
          : null;

      setIsAdmin(Number.isFinite(jobPosition) && jobPosition !== null && jobPosition <= 511);
    });
  }, [getUserRequest, setIsAdmin, setUser]);

  const wfGetUsers = useCallback(
    (centerLat: number, centerLon: number, searchRange: number) => {
      getUsersByLocationRequest({
        centerLat,
        centerLon,
        searchRange,
      }).then((users) => setUsers(users));
    },
    [getUsersByLocationRequest, setUsers]
  );

  const wfUpdateUserLocation = useCallback(
    async (latitude: number, longitude: number) => {
      await patchUserLocationRequest({ latitude, longitude }).then((user) => {
        if (user) {
          setUser(applyOkinichiBranchAlias(user));
        }
      });
    },
    [patchUserLocationRequest, setUser]
  );

  const getUsersByNameLdapPhoneNo = useCallback(
    (name?: string, ldapId?: string, telephoneNumber?: string) => {
      const params = {
        ...(name !== undefined && { name }),
        ...(ldapId !== undefined && { ldapId }),
        ...(telephoneNumber !== undefined && { telephoneNumber }),
      };

      getUsersByNameLdapPhoneNoRequest(params).then((users) => setUsers(users));
    },
    [getUsersByNameLdapPhoneNoRequest, setUsers]
  );

  return {
    wfGetUser,
    wfGetUsers,
    wfUpdateUserLocation,
    getUsersByNameLdapPhoneNo,
  };
}

export default useWfUserService;
